import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MobileBottomNav from "@/components/MobileBottomNav";
import { ArrowRight, CheckCircle2, Users, Clock, Shield, Zap, Droplet, Trees, Truck, Hammer, Sparkles, Wrench, Snowflake, Camera, DoorOpen, PaintBucket, Home, HardHat, Grid3x3, Car, CarFront, PlusCircle, Bell, Briefcase, MessageSquare, FileSearch, TrendingDown, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

const Index = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<"customer" | "professional" | "admin" | "moderator" | null>(null);
  const [checkingWelcome, setCheckingWelcome] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      // CRITICAL: Clear localStorage IMMEDIATELY if no session
      if (!session) {
        console.log("[Index] No session found, clearing state and localStorage");
        setUser(null);
        setUserRole(null);
        localStorage.removeItem("activeRole");
        return;
      }
      
      console.log("[Index] Session found, user:", session.user.id);
      
      // Continue with session logic
      setUser(session.user);
      
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);
      
      if (roleData && roleData.length > 0) {
        // Check if there's a stored active role in localStorage
        const storedRole = localStorage.getItem("activeRole") as "customer" | "professional" | "admin" | "moderator" | null;
        if (storedRole && roleData.some(r => r.role === storedRole)) {
          setUserRole(storedRole);
        } else {
          // Default to professional if available, otherwise first role
          const preferredRole = roleData.find(r => r.role === "professional")?.role || roleData[0].role;
          setUserRole(preferredRole);
        }
      }
    };

    fetchUserData();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log("[Index] Auth state changed, session:", session ? "exists" : "null");
      setUser(session?.user ?? null);
      
      if (session?.user) {
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id);
        
        if (roleData && roleData.length > 0) {
          // Check if there's a stored active role in localStorage
          const storedRole = localStorage.getItem("activeRole") as "customer" | "professional" | "admin" | "moderator" | null;
          if (storedRole && roleData.some(r => r.role === storedRole)) {
            setUserRole(storedRole);
          } else {
            // Default to professional if available, otherwise first role
            const preferredRole = roleData.find(r => r.role === "professional")?.role || roleData[0].role;
            setUserRole(preferredRole);
          }
        }
      } else {
        // Clear userRole and localStorage when there's no session
        console.log("[Index] No session in auth change, clearing state");
        setUserRole(null);
        localStorage.removeItem("activeRole");
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Check if professional should see welcome dialog
  useEffect(() => {
    const checkProfessionalWelcome = async () => {
      if (user && userRole === "professional") {
        setCheckingWelcome(true);
        try {
          const { data } = await supabase
            .from("profiles")
            .select("profile_welcome_seen")
            .eq("id", user.id)
            .maybeSingle();
          
          if (data && !data.profile_welcome_seen) {
            navigate("/profile?welcome=true");
            return;
          }
        } catch (error) {
          console.error("[Index] Error checking welcome status:", error);
        } finally {
          setCheckingWelcome(false);
        }
      }
    };
    
    checkProfessionalWelcome();
  }, [user, userRole, navigate]);

  const handleNewJobClick = () => {
    if (user && userRole === "customer") {
      navigate("/dashboard", { state: { openCreateDialog: true } });
    } else {
      navigate("/auth?register=true&role=customer");
    }
  };

  // Professional view - ONLY show if user is logged in AND has professional role
  if (user && userRole === "professional") {
    // Show loading while checking welcome status
    if (checkingWelcome) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      );
    }
  }

  // Professional view - ONLY show if user is logged in AND has professional role
  if (user && userRole === "professional") {
    return (
      <div className="min-h-screen relative overflow-hidden pb-20 lg:pb-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <Navbar />
        
        {/* Hero Section - Professional */}
        <section className="relative z-10 pt-20 pb-2 px-4 md:pt-24 md:pb-4">
          <div className="container mx-auto max-w-7xl">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-0 text-center">
                <div className="flex items-center justify-center mb-3">
                  <img src="/fixbids-logo-hero.png" alt="FixBids Logo" className="h-[140px] sm:h-[180px] md:h-[220px] lg:h-[260px] w-auto object-contain" width={390} height={260} fetchPriority="high" />
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent">
                  Αυξήστε τα έσοδά σας - Βρείτε δουλειές κάθε μέρα
                </h2>
                <p className="text-base sm:text-lg md:text-xl text-muted-foreground">
                  Δείτε όλες τις λεπτομέρειες δωρεάν, δώστε προσφορά μόνο αν σας ενδιαφέρει, και επικοινωνήστε αμέσως με τον πελάτη. Χωρίς προμήθειες - Κρατάτε 100%!
                </p>
                
                <div className="flex flex-col items-center gap-3 pt-6">
                  <Link to="/jobs">
                    <Button 
                      size="lg"
                      className="h-12 px-8 text-base font-semibold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg"
                    >
                      Δείτε Διαθέσιμες Εργασίες
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <div className="bg-primary/10 backdrop-blur-sm border border-primary/20 rounded-lg px-4 py-3 text-sm md:text-base">
                    <span className="font-medium text-primary">💡 Κάθε προσφορά μόνο 2€</span>
                    <span className="text-muted-foreground block md:inline"> - Χωρίς προμήθειες επί των εσόδων! Κρατάτε το 100%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits for Professionals */}
        <section className="relative z-10 py-6 md:py-10 px-4">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-4 md:mb-8">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">Γιατί να επιλέξετε το FixBids;</h2>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground px-4">
                Περισσότερες δουλειές, περισσότερα έσοδα, μηδενικό κόστος
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-2xl">💰</span>
                </div>
                <h3 className="font-semibold text-lg">Χωρίς Προμήθειες Επί των Εσόδων</h3>
                <p className="text-muted-foreground text-sm">
                  Κρατάτε κάθε ευρώ που κερδίζετε. Μόνο 2€ ανά προσφορά, καμία προμήθεια επί των εσόδων σας!
                </p>
              </Card>

              <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Bell className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Άμεσες Ειδοποιήσεις</h3>
                <p className="text-muted-foreground text-sm">
                  Μάθετε πρώτοι για νέες εργασίες στην ειδικότητά σας και την περιοχή σας.
                </p>
              </Card>

              <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Απευθείας Επικοινωνία</h3>
                <p className="text-muted-foreground text-sm">
                  Μιλήστε απευθείας με τον πελάτη χωρίς μεσάζοντες.
                </p>
              </Card>

              <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <FileSearch className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Πλήρης Διαφάνεια</h3>
                <p className="text-muted-foreground text-sm">
                  Βλέπετε ΟΛΕΣ τις λεπτομέρειες κάθε εργασίας (περιγραφή, φωτογραφίες, τοποθεσία) δωρεάν. Αποφασίζετε εσείς αν θα δώσετε προσφορά.
                </p>
              </Card>

              <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-2xl">🆓</span>
                </div>
                <h3 className="font-semibold text-lg">Χωρίς Κόστος Εγγραφής</h3>
                <p className="text-muted-foreground text-sm">
                  Δωρεάν εγγραφή, χωρίς συνδρομές. Πληρώνετε μόνο όταν στέλνετε προσφορά.
                </p>
              </Card>

              <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <MessageSquare className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Chat Αμέσως Μετά την Προσφορά</h3>
                <p className="text-muted-foreground text-sm">
                  Με το που δώσετε προσφορά, ανοίγει αυτόματα το chat για να συζητήσετε λεπτομέρειες και να κλείσετε το ραντεβού.
                </p>
              </Card>

              <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Χτίστε Φήμη</h3>
                <p className="text-muted-foreground text-sm">
                  Οι αξιολογήσεις σας φέρνουν περισσότερες δουλειές και καλύτερες τιμές.
                </p>
              </Card>

              <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Briefcase className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Διαχείριση Προσφορών</h3>
                <p className="text-muted-foreground text-sm">
                  Όλες οι προσφορές και οι εργασίες σας σε ένα μέρος, εύκολα.
                </p>
              </Card>

              <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Γρήγορη Απόκριση</h3>
                <p className="text-muted-foreground text-sm">
                  Όσο πιο γρήγορα απαντάτε, τόσο περισσότερες δουλειές κερδίζετε.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* How it Works - Professional */}
        <section className="relative z-10 py-10 md:py-14 px-4 bg-gradient-to-br from-muted/30 to-background/50 backdrop-blur-sm">
          <div className="container mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Πώς λειτουργεί</h2>
              <p className="text-muted-foreground">Απλά 4 βήματα για να ξεκινήσετε</p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto">
                  1
                </div>
                <h3 className="text-lg font-semibold">Εγγραφείτε Δωρεάν</h3>
                <p className="text-muted-foreground text-sm">
                  Ολοκληρώστε το προφίλ σας με την ειδικότητα και την περιοχή σας
                </p>
              </div>
              
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto">
                  2
                </div>
                <h3 className="text-lg font-semibold">Λάβετε Ειδοποιήσεις & Δείτε Λεπτομέρειες</h3>
                <p className="text-muted-foreground text-sm">
                  Θα σας ενημερώνουμε για νέες εργασίες και μπορείτε να δείτε όλες τις λεπτομέρειες δωρεάν πριν αποφασίσετε
                </p>
              </div>
              
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto">
                  3
                </div>
                <h3 className="text-lg font-semibold">Υποβάλετε Προσφορά</h3>
                <p className="text-muted-foreground text-sm">
                  Στείλτε την προσφορά σας με μόλις 2€ και ανοίξτε αμέσως το chat με τον πελάτη
                </p>
                <p className="text-xs text-primary font-medium">
                  💬 Chat άμεσα μετά την προσφορά!
                </p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto">
                  4
                </div>
                <h3 className="text-lg font-semibold">Κλείστε τη Δουλειά</h3>
                <p className="text-muted-foreground text-sm">
                  Επικοινωνήστε απευθείας και ολοκληρώστε την εργασία
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="relative z-10 py-6 md:py-10 px-4">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-4 md:mb-8">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">Βρείτε δουλειές στην ειδικότητά σας</h2>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground px-4">
                Εκατοντάδες εργασίες κάθε μέρα σε όλη την Ελλάδα
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Zap className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                </div>
                <h3 className="text-sm md:text-base font-semibold text-center mb-1">Ηλεκτρολόγος</h3>
              </Card>
              
              <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Droplet className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                </div>
                <h3 className="text-sm md:text-base font-semibold text-center mb-1">Υδραυλικός</h3>
              </Card>
              
              <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Hammer className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                </div>
                <h3 className="text-sm md:text-base font-semibold text-center mb-1">Ανακαινήσεις</h3>
              </Card>
              
              <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                  <PaintBucket className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                </div>
                <h3 className="text-xs md:text-base font-semibold text-center mb-1 px-1 break-words">Ελαιοχρωματιστής</h3>
              </Card>

              <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Snowflake className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                </div>
                <h3 className="text-sm md:text-base font-semibold text-center mb-1">Ψυκτικός/Κλιματισμός</h3>
              </Card>

              <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Wrench className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                </div>
                <h3 className="text-sm md:text-base font-semibold text-center mb-1">Αποφράξεις</h3>
              </Card>

              <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Trees className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                </div>
                <h3 className="text-sm md:text-base font-semibold text-center mb-1">Κηπουρικά</h3>
              </Card>

              <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Truck className="w-6 h-6 md:w-8 md:h-8 text-primary" />
              </div>
              <h3 className="text-sm md:text-base font-semibold text-center mb-1">Μεταφορές</h3>
            </Card>
          </div>
          
          <div className="text-center mt-6">
            <p className="text-sm md:text-base text-muted-foreground/80 italic">
              ...και δεκάδες άλλες ειδικότητες για κάθε σας ανάγκη
            </p>
          </div>
        </div>
      </section>

        {/* Testimonials - Professionals */}
        <section className="relative z-10 py-10 md:py-14 px-4 bg-gradient-to-br from-muted/30 to-background/50 backdrop-blur-sm">
          <div className="container mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Τι λένε οι επαγγελματίες</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <Card className="p-6 space-y-4 bg-background/90 backdrop-blur-sm border-primary/10 hover:shadow-2xl transition-all hover:scale-105">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-xl">
                    ΓΠ
                  </div>
                  <div>
                    <div className="font-semibold text-lg">Γιώργος Π.</div>
                    <div className="text-sm text-muted-foreground">Υδραυλικός • Θεσσαλονίκη</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-yellow-500 flex text-lg">
                    {"★★★★★".split("").map((star, i) => (
                      <span key={i}>{star}</span>
                    ))}
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  "Το FixBids με βοήθησε να βρω νέους πελάτες χωρίς προμήθειες. Κάθε εβδομάδα παίρνω 3-4 νέες δουλειές. Η επικοινωνία είναι άμεση και οι πελάτες ξέρουν ακριβώς τι θέλουν."
                </p>
              </Card>
              
              <Card className="p-6 space-y-4 bg-background/90 backdrop-blur-sm border-primary/10 hover:shadow-2xl transition-all hover:scale-105">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center text-secondary font-bold text-xl">
                    ΝΚ
                  </div>
                  <div>
                    <div className="font-semibold text-lg">Νίκος Κ.</div>
                    <div className="text-sm text-muted-foreground">Ηλεκτρολόγος • Αθήνα</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-yellow-500 flex text-lg">
                    {"★★★★★".split("").map((star, i) => (
                      <span key={i}>{star}</span>
                    ))}
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  "Πλέον δεν πληρώνω προμήθειες σε κανένα. Κρατάω όλα τα χρήματα και έχω διπλασιάσει τα έσοδά μου. Οι ειδοποιήσεις είναι άμεσες και μπορώ να απαντήσω γρήγορα!"
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ - Professional */}
        <section className="relative z-10 py-8 md:py-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold mb-2 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Συχνές Ερωτήσεις</h2>
            </div>
            
            <div className="space-y-4">
              <Card className="p-6 bg-background/95 backdrop-blur-sm border-primary/20 hover:shadow-xl transition-all hover:scale-[1.02] group">
                <div className="flex gap-4 items-center">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Bell className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-2">Πώς μου έρχονται οι εργασίες;</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Λαμβάνετε άμεσες ειδοποιήσεις για νέες εργασίες που ταιριάζουν στην ειδικότητά σας και την περιοχή σας. Όσο πιο γρήγορα απαντήσετε, τόσο περισσότερες πιθανότητες έχετε να κερδίσετε τη δουλειά.
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 bg-background/95 backdrop-blur-sm border-primary/20 hover:shadow-xl transition-all hover:scale-[1.02] group">
                <div className="flex gap-4 items-center">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <DoorOpen className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-2">Πληρώνω προμήθεια;</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Όχι! Το FixBids είναι 100% δωρεάν για επαγγελματίες. Δεν υπάρχουν προμήθειες, κρυφά κόστη ή συνδρομές. Κρατάτε 100% των εσόδων σας.
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 bg-background/95 backdrop-blur-sm border-primary/20 hover:shadow-xl transition-all hover:scale-[1.02] group">
                <div className="flex gap-4 items-center">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-2">Πώς χτίζω τη φήμη μου;</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Μετά από κάθε ολοκληρωμένη εργασία, οι πελάτες μπορούν να σας αξιολογήσουν. Όσο καλύτερες αξιολογήσεις έχετε, τόσο περισσότερες δουλειές θα κερδίζετε και με καλύτερες τιμές.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-background/95 backdrop-blur-sm border-primary/20 hover:shadow-xl transition-all hover:scale-[1.02] group">
                <div className="flex gap-4 items-center">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <CheckCircle2 className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-2">Πόσες προσφορές μπορώ να στείλω;</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Όσες θέλετε! Δεν υπάρχει όριο στον αριθμό των προσφορών που μπορείτε να υποβάλετε. Στείλτε προσφορές σε όλες τις εργασίες που σας ενδιαφέρουν.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 py-16 md:py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <Card className="relative overflow-hidden p-4 sm:p-6 md:p-10 text-center space-y-6 bg-gradient-to-br from-background to-muted/30 shadow-xl border-2 border-primary/20">
              <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative z-10">
                <div className="inline-block mb-4">
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto">
                    <Wrench className="w-7 h-7 text-primary" />
                  </div>
                </div>
                
                <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 text-primary px-2">
                  Ξεκινήστε να κερδίζετε σήμερα
                </h2>
                
                <p className="text-sm sm:text-base md:text-lg text-foreground/70 max-w-2xl mx-auto mb-8 leading-relaxed px-2">
                  Εγγραφείτε δωρεάν και δείτε αμέσως διαθέσιμες εργασίες στην περιοχή σας. Χωρίς προμήθειες, χωρίς κρυφά κόστη.
                </p>
                
                <Link to="/dashboard">
                  <Button 
                    size="lg" 
                    className="h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base font-bold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl hover:shadow-primary/30 transition-all hover:scale-105 group"
                  >
                    <span className="truncate">Δείτε Διαθέσιμες Εργασίες</span>
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                  </Button>
                </Link>
                
                <p className="text-[10px] sm:text-xs md:text-sm text-foreground/60 mt-6 font-medium px-2">
                  <span className="inline-block">100% Δωρεάν</span>
                  <span className="mx-1">•</span>
                  <span className="inline-block">Χωρίς Προμήθειες</span>
                  <span className="mx-1">•</span>
                  <span className="inline-block">Κρατάτε 100% των Εσόδων</span>
                </p>
              </div>
            </Card>
          </div>
        </section>
        
        <Footer />
        <MobileBottomNav />
      </div>
    );
  }

  // Default view for customers and non-logged in users
  return (
    <div className="min-h-screen relative overflow-hidden pb-20 lg:pb-0">
      {/* Premium Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
      
      <Navbar />
      
      {/* Hero Section - Mobile Optimized */}
      <section className="relative z-10 pt-20 pb-2 px-4 md:pt-24 md:pb-4">
        <div className="container mx-auto max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-0 text-center">
              <div className="flex items-center justify-center mb-3">
                <img src="/fixbids-logo-hero.png" alt="FixBids Logo" className="h-[140px] sm:h-[180px] md:h-[220px] lg:h-[260px] w-auto object-contain" width={390} height={260} fetchPriority="high" />
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent">
                Περιγράψε αυτό που χρειάζεσαι — βρες τον κατάλληλο τεχνικό στη στιγμή!
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground">
                Δημοσίευσε την εργασία ή βλάβη σου σε 1 λεπτό και πέτυχε την καλύτερη τιμή από επαγγελματίες της περιοχής σου – χωρίς τηλέφωνα, χωρίς μεσάζοντες, χωρίς προμήθειες.
              </p>
              
              {!user && (
                <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full sm:w-auto justify-center pt-4">
                  <Link to="/auth?register=true&role=customer" className="w-full sm:w-auto">
                    <Button size="lg" className="gap-2 w-full sm:w-auto h-12 md:h-11 text-base bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg">
                      Ξεκίνα Δωρεάν Τώρα
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/auth?register=true&role=professional" className="w-full sm:w-auto">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto h-12 md:h-11 text-base">
                      Είστε τεχνικός; Εγγραφείτε εδώ
                    </Button>
                  </Link>
                </div>
              )}
              
              {user && (
                <div className="flex justify-center pt-6">
                  <Button 
                    size="lg" 
                    onClick={handleNewJobClick}
                    className="h-12 px-8 text-base font-semibold bg-primary hover:bg-primary/90 shadow-lg"
                  >
                    ΝΕΑ ΕΡΓΑΣΙΑ
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="relative z-10 py-10 md:py-14 px-4 bg-gradient-to-br from-muted/30 to-background/50 backdrop-blur-sm">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Πώς λειτουργεί το FixBids</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto">
                1
              </div>
              <h3 className="text-xl font-semibold">Ανεβάζετε τη δουλειά σας</h3>
              <p className="text-muted-foreground">
                Περιγράψτε τι χρειάζεστε και προσθέστε φωτογραφίες
              </p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto">
                2
              </div>
              <h3 className="text-xl font-semibold">Λαμβάνετε Προσφορές</h3>
              <p className="text-muted-foreground">
                Οι τεχνικοί υποβάλλουν προσφορές με κόστος και χρόνο ολοκλήρωσης
              </p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto">
                3
              </div>
              <h3 className="text-xl font-semibold">Επικοινωνείτε & Διαλέγετε</h3>
              <p className="text-muted-foreground">
                Επικοινωνήστε με όλους τους τεχνικούς που έδωσαν προσφορά και επιλέξτε τον καλύτερο
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section - Mobile Optimized */}
      <section className="relative z-10 py-6 md:py-10 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-4 md:mb-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">Κατηγορίες Τεχνικών</h2>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground px-4">
              Βρείτε τον κατάλληλο επαγγελματία για την εργασία σας
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Zap className="w-6 h-6 md:w-8 md:h-8 text-primary" />
              </div>
              <h3 className="text-sm md:text-base font-semibold text-center mb-1">Ηλεκτρολόγος</h3>
            </Card>
            
            <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Droplet className="w-6 h-6 md:w-8 md:h-8 text-primary" />
              </div>
              <h3 className="text-sm md:text-base font-semibold text-center mb-1">Υδραυλικός</h3>
            </Card>
            
            <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Hammer className="w-6 h-6 md:w-8 md:h-8 text-primary" />
              </div>
              <h3 className="text-sm md:text-base font-semibold text-center mb-1">Ανακαινήσεις</h3>
            </Card>
            
            <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <PaintBucket className="w-6 h-6 md:w-8 md:h-8 text-primary" />
              </div>
              <h3 className="text-xs md:text-base font-semibold text-center mb-1 px-1 break-words">Ελαιοχρωματιστής</h3>
            </Card>

            <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Snowflake className="w-6 h-6 md:w-8 md:h-8 text-primary" />
              </div>
              <h3 className="text-sm md:text-base font-semibold text-center mb-1">Ψυκτικός/Κλιματισμός</h3>
            </Card>

            <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Wrench className="w-6 h-6 md:w-8 md:h-8 text-primary" />
              </div>
              <h3 className="text-sm md:text-base font-semibold text-center mb-1">Αποφράξεις</h3>
            </Card>

            <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Trees className="w-6 h-6 md:w-8 md:h-8 text-primary" />
              </div>
              <h3 className="text-sm md:text-base font-semibold text-center mb-1">Κηπουρικά</h3>
            </Card>

            <Card className="p-4 hover:shadow-2xl transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Truck className="w-6 h-6 md:w-8 md:h-8 text-primary" />
              </div>
              <h3 className="text-sm md:text-base font-semibold text-center mb-1">Μεταφορές</h3>
            </Card>
          </div>
          
          <div className="text-center mt-6">
            <p className="text-sm md:text-base text-muted-foreground/80 italic">
              ...και δεκάδες άλλες ειδικότητες για κάθε σας ανάγκη
            </p>
          </div>
        </div>
      </section>

      {/* Features Section - Mobile Optimized */}
      <section className="relative z-10 py-6 md:py-12 px-4 bg-gradient-to-br from-muted/30 to-background/50 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-4 md:mb-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">Γιατί να επιλέξετε το FixBids;</h2>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground px-4">
              Βρίσκετε τεχνικό με διαφάνεια – χωρίς μεσάζοντες ή προμήθειες
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-6">
            <Card className="p-4 md:p-6 text-center space-y-2 md:space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-sm md:text-lg leading-tight">Αξιολογήσεις</h3>
              <p className="text-muted-foreground text-xs md:text-sm hidden md:block">
                Όλοι οι τεχνικοί έχουν αξιολογήσεις από προηγούμενες δουλειές
              </p>
            </Card>
            
            <Card className="p-4 md:p-6 text-center space-y-2 md:space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Clock className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-sm md:text-lg leading-tight">Γρήγορη Ανταπόκριση</h3>
              <p className="text-muted-foreground text-xs md:text-sm hidden md:block">
                Οι τεχνικοί απαντούν μέσα σε λίγες ώρες
              </p>
            </Card>
            
            <Card className="p-4 md:p-6 text-center space-y-2 md:space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Shield className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-sm md:text-lg leading-tight">Προστασία Δεδομένων</h3>
              <p className="text-muted-foreground text-xs md:text-sm hidden md:block">
                Τα προσωπικά σας δεδομένα παραμένουν προστατευμένα
              </p>
            </Card>
            
            <Card className="p-4 md:p-6 text-center space-y-2 md:space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Users className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-sm md:text-lg leading-tight">Διαφάνεια</h3>
              <p className="text-muted-foreground text-xs md:text-sm hidden md:block">
                Οι αξιολογήσεις διασφαλίζουν τη σωστή επιλογή
              </p>
            </Card>
            
            <Card className="p-4 md:p-6 text-center space-y-2 md:space-y-4 hover:shadow-lg transition-all hover:scale-105 bg-background/90 backdrop-blur-sm border-primary/10">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <TrendingDown className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-sm md:text-lg leading-tight">Καλύτερες Τιμές</h3>
              <p className="text-muted-foreground text-xs md:text-sm hidden md:block">
                Οι τεχνικοί μας δεν πληρώνουν προμήθεια επί των εσόδων, άρα παίρνετε τις καλύτερες τιμές της αγοράς!
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="relative z-10 py-10 md:py-14 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Τι λένε οι πελάτες μας</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="p-6 space-y-4 bg-background/90 backdrop-blur-sm border-primary/10 hover:shadow-2xl transition-all hover:scale-105">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-xl">
                  ΜΚ
                </div>
                <div>
                  <div className="font-semibold text-lg">Μαρία Κ.</div>
                  <div className="text-sm text-muted-foreground">Αθήνα</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="text-yellow-500 flex text-lg">
                  {"★★★★★".split("").map((star, i) => (
                    <span key={i}>{star}</span>
                  ))}
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                "Ανέβασα μια εργασία για ηλεκτρολογικά και μέσα σε 3 ώρες είχα 5 προσφορές! Επέλεξα τον πιο κατάλληλο με βάση τις αξιολογήσεις και το κόστος. Εξαιρετική εμπειρία!"
              </p>
            </Card>
            
            <Card className="p-6 space-y-4 bg-background/90 backdrop-blur-sm border-primary/10 hover:shadow-2xl transition-all hover:scale-105">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center text-secondary font-bold text-xl">
                  ΓΠ
                </div>
                <div>
                  <div className="font-semibold text-lg">Γιώργος Π.</div>
                  <div className="text-sm text-muted-foreground">Θεσσαλονίκη</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="text-yellow-500 flex text-lg">
                  {"★★★★★".split("").map((star, i) => (
                    <span key={i}>{star}</span>
                  ))}
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                "Ως υδραυλικός, το FixBids με βοήθησε να βρω νέους πελάτες χωρίς προμήθειες. Η επικοινωνία είναι άμεση και οι πελάτες ξέρουν ακριβώς τι θέλουν."
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative z-10 py-8 md:py-12 px-4 bg-gradient-to-br from-muted/30 to-background/50 backdrop-blur-sm">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Συχνές Ερωτήσεις</h2>
            <p className="text-muted-foreground">Όλα όσα χρειάζεται να ξέρετε για το FixBids</p>
          </div>
          
          <div className="space-y-4">
            <Card className="p-6 bg-background/95 backdrop-blur-sm border-primary/20 hover:shadow-xl transition-all hover:scale-[1.02] group">
              <div className="flex gap-4 items-center">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <DoorOpen className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-2">Πόσο κοστίζει η χρήση του FixBids;</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Το FixBids είναι 100% δωρεάν για πελάτες. Οι επαγγελματίες μπορούν να εγγραφούν και να υποβάλλουν προσφορές χωρίς καμία προμήθεια.
                  </p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6 bg-background/95 backdrop-blur-sm border-primary/20 hover:shadow-xl transition-all hover:scale-[1.02] group">
              <div className="flex gap-4 items-center">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-2">Πόσο γρήγορα θα λάβω προσφορές;</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Συνήθως οι πρώτες προσφορές έρχονται μέσα σε λίγες ώρες. Όσο πιο λεπτομερής είναι η περιγραφή σας, τόσο πιο ακριβείς θα είναι οι προσφορές.
                  </p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6 bg-background/95 backdrop-blur-sm border-primary/20 hover:shadow-xl transition-all hover:scale-[1.02] group">
              <div className="flex gap-4 items-center">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-2">Πώς επιλέγω τον κατάλληλο τεχνικό;</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Μπορείτε να δείτε όλες τις προσφορές, τις αξιολογήσεις των τεχνικών από προηγούμενες δουλειές, και να επικοινωνήσετε απευθείας μέσω της εφαρμογής πριν αποφασίσετε.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <Card className="relative overflow-hidden p-6 md:p-10 text-center space-y-6 bg-gradient-to-br from-background to-muted/30 shadow-xl border-2 border-primary/20">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <div className="inline-block mb-4">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto">
                  <Wrench className="w-7 h-7 text-primary" />
                </div>
              </div>
              
              <h2 className="text-3xl md:text-5xl font-bold mb-4 text-primary">
                Έτοιμοι να ξεκινήσετε;
              </h2>
              
              <p className="text-base md:text-lg text-foreground/70 max-w-2xl mx-auto mb-8 leading-relaxed">
                Δημιουργήστε τώρα την πρώτη σας εργασία και λάβετε προσφορές μέσα σε λίγες ώρες – χωρίς τηλέφωνα, χωρίς προμήθεια, 100% δωρεάν!
              </p>
              
              <div className="flex justify-center">
                <Link to="/auth?register=true">
                  <Button 
                    size="lg" 
                    className="h-14 px-8 text-base font-bold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl hover:shadow-primary/30 transition-all hover:scale-105 group"
                  >
                    <span>Ξεκινήστε Δωρεάν</span>
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
              
              <p className="text-sm md:text-base text-foreground/60 mt-6 font-medium">
                Άμεση εγγραφή • Δωρεάν για πάντα
              </p>
            </div>
          </Card>
        </div>
      </section>
      
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default Index;
